"use client";

import React, { useEffect, useState } from "react";
import { Sparkles, CircleCheck, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(({
  className,
  ...props
}, ref) => {
  return <input ref={ref} className={`
          border-secondary/30 placeholder:text-muted-foreground/40 focus-visible:border-secondary focus-visible:ring-0 
          aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive 
          dark:bg-transparent flex h-12 w-full rounded-none border-b border-t-0 border-x-0 bg-transparent px-0 py-2 text-base shadow-none 
          transition-all outline-none focus-visible:ring-0 disabled:cursor-not-allowed 
          disabled:opacity-50 md:text-sm font-sans
          ${className || ""}
        `} {...props} />;
});
Input.displayName = "Input";
export default function InquiryForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="58000" id="inquiry-form" className="dark bg-background text-foreground py-20 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-30">
        <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] bg-primary/10 rounded-lg blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] bg-secondary/10 rounded-lg blur-[120px]" />
      </div>

      <div ref={ref} className={`max-w-3xl mx-auto relative z-10 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="text-center mb-12 space-y-4">
          <div className="inline-flex items-center justify-center gap-2 text-secondary mb-2">
            <Sparkles className="w-4 h-4" />
            <span className="uppercase tracking-[0.3em] text-xs font-mono font-medium">
              Midnight Soul & Golden Hour
            </span>
            <Sparkles className="w-4 h-4" />
          </div>
          <h2 className="text-5xl md:text-7xl font-serif font-light tracking-tight text-foreground italic">
            Secure the Soul
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-lg font-light leading-relaxed">Planning a intimate event or a large scales corporate gala? Tell us about your vision, and Synergy Sound will curate a customized musical experience that resonates to your exact needs.</p>
        </div>

        <div className="bg-transparent backdrop-blur-none border-none p-6 md:p-0">
          <FormProvider formKey="event_inquiry_form_home" formId="event_inquiry_form_home" sectionName="inquire-for-your-event" defaultValues={{
          name: "",
          email: "",
          phone: "",
          service: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: false
          },
          service: {
            required: true
          },
          message: {
            required: true,
            minLength: 10
          }
        }} submitText="Request a Performance" successMessage="Thank you. Your request has been received. Our team will contact you shortly to discuss the rhythm of your event.">
            <InquiryFormContent />
          </FormProvider>
        </div>
      </div>
      <Toaster />
    </section>;
}
function InquiryFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-700">
        <div className="w-24 h-24 rounded-lg bg-secondary/5 flex items-center justify-center mb-8 border border-secondary/20">
          <CircleCheck className="w-12 h-12 text-secondary" />
        </div>
        <h3 className="text-3xl font-serif text-secondary mb-4 italic">
          Request Received
        </h3>
        <p className="text-muted-foreground mb-10 max-w-md font-light leading-relaxed">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-secondary/40 text-secondary hover:bg-secondary hover:text-secondary-foreground px-8 font-mono uppercase tracking-widest text-xs transition-all duration-500">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
        {}
        <FormField control={control} name="name" rules={{
        required: "Your name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono uppercase tracking-[0.2em] text-[10px]">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Coltrane" {...field} />
              </FormControl>
              <FormMessage className="font-mono text-[10px] uppercase pt-1" />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono uppercase tracking-[0.2em] text-[10px]">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@jazz.com" {...field} />
              </FormControl>
              <FormMessage className="font-mono text-[10px] uppercase pt-1" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono uppercase tracking-[0.2em] text-[10px]">
                Phone Number <span className="text-muted-foreground/50 lowercase italic ml-1">(optional)</span>
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(310) 555-0123" {...field} />
              </FormControl>
              <FormDescription className="text-[10px] font-mono uppercase opacity-40">
                Los Angeles or International
              </FormDescription>
              <FormMessage className="font-mono text-[10px] uppercase pt-1" />
            </FormItem>} />

        {}
        <FormField control={control} name="service" rules={{
        required: "Please select an event type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono uppercase tracking-[0.2em] text-[10px]">
                Event Type
              </FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="w-full border-b border-t-0 border-x-0 bg-transparent px-0 border-secondary/30 h-12 focus:ring-0">
                    <SelectValue placeholder="Select an event type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-card border-border/50">
                  {["Anniversary/Wedding", "Milestone Birthday",, "Bar Mitzvah", "Quinceañera", "Corporate Gala", "Private Soirée", "High-End Lounge Performance", "Other Custom Event"].map((option, i) => <SelectItem key={option} value={option} data-index={i} className="font-sans focus:bg-primary focus:text-primary-foreground">
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage className="font-mono text-[10px] uppercase pt-1" />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="message" rules={{
      required: "Share the details of your vision"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-mono uppercase tracking-[0.2em] text-[10px]">Tell Us Your Vision</FormLabel>
            <FormControl>
              <Textarea placeholder="Share details about your event date, venue, and the soulful vibe you're looking for..." className="min-h-[120px] resize-none border-b border-t-0 border-x-0 px-0 bg-transparent border-secondary/30 focus-visible:ring-0 focus-visible:border-secondary transition-all font-sans" {...field} />
            </FormControl>
            <FormMessage className="font-mono text-[10px] uppercase pt-1" />
          </FormItem>} />

      {}
      {submitError && <div className="p-4 bg-destructive/5 border border-destructive/20 animate-in fade-in slide-in-from-top-2">
          <p className="text-destructive font-mono text-[10px] uppercase tracking-wider text-center">{submitError}</p>
        </div>}

      {}
      <div className="pt-8 flex justify-center md:justify-start">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto md:min-w-[280px] h-14 bg-foreground text-background hover:bg-foreground/90 font-mono text-xs uppercase tracking-[0.3em] transition-all duration-500 shadow-xl">
          {isSubmitting ? <>
              <Loader className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </> : "Request a Performance"}
        </Button>
      </div>
    </Form>;
}